import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { useQuery, useMutation } from 'react-query'

import {
  Button,
  Badge,
  VStack,
  HStack,
  Box,
  Divider,
  Text,
  useDisclosure,
  useToast,
  useClipboard,
  Spinner,
  Flex
} from '@chakra-ui/react'

import { ModalConfirmConsultTime } from '../Modal/confirmConsultTime'
import { apiAuth } from '../../services/apiAuth'
import { Input } from '../Form/Input'
import { SelectComponent } from '../Form/SelectComponent'
import { Textarea } from '../Form/Textarea'
import { AvailableTimeSlotObject, AvailableTimeSlotResponseItem } from '../../types/equipment'
import { AvailableEquipmentsByGroupItem } from '../../types/equipment'
import { useAuth } from '../../hooks/auth'
import { formatDateBRFull, formatHour, formatToDateInput } from '../../utils/timezone'

interface Option {
  label: string
  value: number
}

const STORAGE_KEY = 'slots'

function isSlotObject(item: AvailableTimeSlotResponseItem): item is AvailableTimeSlotObject {
  return typeof item === 'object' && item !== null && 'arrival_time' in item
}

function formatEquipmentGroupLine(group: AvailableEquipmentsByGroupItem) {
  const equipmentNames = (group.equipments || []).map((equipment) => equipment.name).join(', ')

  return equipmentNames ? `${group.group_name}: ${equipmentNames}` : group.group_name
}

export const AvailableSlotsCard: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const toast = useToast()
  const { decodeToken } = useAuth()
  const clinicTimezone = decodeToken.clinic.timezone

  const [specialist, setSpecialist] = useState<Option | null>(null)
  const [services, setServices] = useState<Option[]>([])
  const [equipmentGroups, setEquipmentGroups] = useState<Option[]>([])
  const [visualSlots, setVisualSlots] = useState<AvailableTimeSlotObject[]>([])

  const [slotInterval, setSlotInterval] = useState(30)
  const [initialDate, setInitialDate] = useState(new Date().toISOString().split('T')[0])
  const [finalDate, setFinalDate] = useState(
    new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  )

  const [startHour, setStartHour] = useState('08:00')
  const [finishHour, setFinishHour] = useState('18:00')

  const [patientName, setPatientName] = useState('')
  const [phone, setPhone] = useState('')

  const [resultText, setResultText] = useState('')
  const [messagePreview, setMessagePreview] = useState('')

  const { onCopy } = useClipboard(messagePreview)

  const formatWhatsappHourFromDate = (dateIso: string) =>
    formatHour(dateIso, clinicTimezone).replace(/:00$/, 'h').replace(':', 'h')

  const groupSlotsByDate = (slots: AvailableTimeSlotObject[]) => {
    const todayKey = formatToDateInput(new Date(), clinicTimezone)
    const tomorrowDate = new Date(`${todayKey}T12:00:00Z`)
    tomorrowDate.setUTCDate(tomorrowDate.getUTCDate() + 1)
    const tomorrowKey = tomorrowDate.toISOString().split('T')[0]
    const groups = new Map<string, AvailableTimeSlotObject[]>()

    slots.forEach((slot) => {
      const dateKey = formatToDateInput(slot.arrival_time, clinicTimezone)
      groups.set(dateKey, [...(groups.get(dateKey) || []), slot])
    })

    return Array.from(groups.entries())
      .sort(([firstDate], [secondDate]) => firstDate.localeCompare(secondDate))
      .map(([dateKey, dateSlots]) => {
        const orderedSlots = [...dateSlots].sort(
          (first, second) =>
            new Date(first.arrival_time).getTime() - new Date(second.arrival_time).getTime()
        )
        const dateLabel = formatDateBRFull(dateSlots[0].arrival_time, clinicTimezone)
        const relativeLabel =
          dateKey === todayKey ? 'Hoje, ' : dateKey === tomorrowKey ? 'Amanhã, ' : ''

        return {
          dateKey,
          title: `${relativeLabel}${dateLabel}:`,
          slots: orderedSlots
        }
      })
  }

  const buildMessageTextFromObjectSlots = (slots: AvailableTimeSlotObject[]) =>
    groupSlotsByDate(slots)
      .map(
        (group) =>
          `${group.title}\n${group.slots
            .map((slot) => formatWhatsappHourFromDate(slot.arrival_time))
            .join('\n')}`
      )
      .join('\n\n')

  const buildMessagePreview = (text: string) => `${patientName || ''} ✨

Temos estes horários disponíveis:

${text}

Se sim, qual deles funciona melhor para si?`

  const maxFinalDateObj = new Date(initialDate)
  if (!isNaN(maxFinalDateObj.getTime())) {
    maxFinalDateObj.setUTCDate(maxFinalDateObj.getUTCDate() + 30)
  }
  const maxFinalDateLimit =
    initialDate && !isNaN(maxFinalDateObj.getTime())
      ? maxFinalDateObj.toISOString().split('T')[0]
      : ''

  useEffect(() => {
    if (initialDate && finalDate && maxFinalDateLimit) {
      if (finalDate > maxFinalDateLimit) {
        setFinalDate(maxFinalDateLimit)
      } else if (finalDate < initialDate) {
        setFinalDate(initialDate)
      }
    }
  }, [initialDate, finalDate, maxFinalDateLimit])

  /*
  ----------------------------
  Persistência
  ----------------------------
  */

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)

    if (saved) {
      const parsed = JSON.parse(saved)

      setSpecialist(parsed.specialist)
      setServices(parsed.services)
      if (parsed.slotInterval) setSlotInterval(Math.max(15, parsed.slotInterval))
      if (parsed.initialDate) setInitialDate(parsed.initialDate)
      if (parsed.finalDate) setFinalDate(parsed.finalDate)
      setStartHour(parsed.startHour)
      setFinishHour(parsed.finishHour)
    }
  }, [])

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        specialist,
        services,
        slotInterval,
        initialDate,
        finalDate,
        startHour,
        finishHour
      })
    )
  }, [specialist, services, slotInterval, initialDate, finalDate, startHour, finishHour])

  /*
  ----------------------------
  Especialistas
  ----------------------------
  */
  const { data: specialists } = useQuery({
    queryKey: ['specialists'],
    queryFn: async () => {
      const { data } = await apiAuth.get('users/attendances/', {
        params: {
          user__is_active: true,
          is_specialist: true,
          permission_type: '2'
        }
      })

      return data?.results.map((user: any) => ({
        label: user.user.name,
        value: user.user.id
      }))
    },
    staleTime: 1000 * 60 * 30
  })

  /*
  ----------------------------
  Serviços
  ----------------------------
  */

  const { data: servicesOptions } = useQuery({
    queryKey: ['servicesConsultHours'],
    queryFn: async () => {
      const { data } = await apiAuth.get('clinics/services/', {
        params: {
          noPaginate: 1,
          is_active: true
        }
      })

      return data.map((service: any) => ({
        label: service.name,
        value: service.id
      }))
    },
    staleTime: 1000 * 60 * 30
  })

  const { data: equipmentGroupOptions } = useQuery({
    queryKey: ['equipment-groups', 'available-times'],
    queryFn: async () => {
      const { data } = await apiAuth.get('clinics/equipment-groups/', {
        params: { is_active: true, noPaginate: 1 }
      })
      return (data?.results || data || []).map((group: any) => ({
        label: group.name,
        value: group.id
      }))
    }
  })

  /*
  ----------------------------
  Consulta
  ----------------------------
  */

  const timeToMinutes = (time: string) => {
    const normalized = time
      .trim()
      .replace('h', ':')
      .replace(/[^\d:]/g, '')

    const [hour, minute = '0'] = normalized.split(':')

    return Number(hour) * 60 + Number(minute)
  }

  const extractTimeFromLine = (line: string) => {
    const match = line.match(/\b\d{1,2}(h|:)\d{0,2}\b|\b\d{1,2}h\b/)

    return match?.[0] ?? null
  }

  const removeCloseTimes = (items: string[], interval: number) => {
    const filtered: string[] = []
    let lastAcceptedMinutes: number | null = null

    items.forEach((item) => {
      const time = extractTimeFromLine(item)

      /**
       * Se não for uma linha de horário, mantém.
       * Ex: "Hoje, quarta-feira..."
       */
      if (!time) {
        filtered.push(item)
        lastAcceptedMinutes = null
        return
      }

      const currentMinutes = timeToMinutes(time)

      if (lastAcceptedMinutes === null) {
        filtered.push(item)
        lastAcceptedMinutes = currentMinutes
        return
      }

      const diff = currentMinutes - lastAcceptedMinutes

      /**
       * Exemplo:
       * posição 0 = 08h
       * posição 1 = 08h10
       * intervalo = 30
       *
       * remove 08h10 e compara o próximo horário com 08h.
       */
      if (diff >= interval) {
        filtered.push(item)
        lastAcceptedMinutes = currentMinutes
      }
    })

    return filtered
  }

  const removeCloseObjectSlots = (slots: AvailableTimeSlotObject[], interval: number) =>
    groupSlotsByDate(slots).flatMap((group) => {
      let lastAcceptedMinutes: number | null = null

      return group.slots.filter((slot) => {
        const currentMinutes = timeToMinutes(formatHour(slot.arrival_time, clinicTimezone))

        if (lastAcceptedMinutes === null || currentMinutes - lastAcceptedMinutes >= interval) {
          lastAcceptedMinutes = currentMinutes
          return true
        }

        return false
      })
    })

  const searchMutation = useMutation({
    mutationKey: ['available-times'],
    mutationFn: async (payload: any) => {
      const { data } = await apiAuth.post('clinics/attendances/available-times/', payload)

      return data
    },

    onSuccess(data) {
      const apiSlots: AvailableTimeSlotResponseItem[] = Array.isArray(data) ? data : []
      const objects = apiSlots.filter(isSlotObject)
      const strings = apiSlots.filter((slot): slot is string => typeof slot === 'string')
      const filteredObjectSlots = removeCloseObjectSlots(objects, slotInterval)
      const filteredStringSlots = removeCloseTimes(strings, slotInterval)
      const hasObjectSlots = objects.length > 0
      const filteredSlots = hasObjectSlots ? filteredObjectSlots : filteredStringSlots

      if (!filteredSlots.length) {
        setResultText('')
        setMessagePreview('')
        setVisualSlots([])

        toast({
          title: 'Nenhum horário disponível',
          description:
            'Não encontramos horários disponíveis para o especialista e serviço selecionados.',
          status: 'warning'
        })

        return
      }

      setVisualSlots(hasObjectSlots ? filteredObjectSlots : [])
      const text = hasObjectSlots
        ? buildMessageTextFromObjectSlots(filteredObjectSlots)
        : filteredStringSlots.join('\n')

      setResultText(text)
      setMessagePreview(buildMessagePreview(text))

      toast({
        title: 'Horários encontrados',
        status: 'success'
      })
    }
  })

  const handleSearch = () => {
    if (!specialist?.value) {
      toast({
        title: 'Selecione um especialista',
        description: 'Escolha um especialista antes de buscar horários.',
        status: 'warning'
      })

      return
    }

    if (!services.length) {
      toast({
        title: 'Selecione pelo menos um serviço',
        description: 'Escolha um serviço antes de buscar horários.',
        status: 'warning'
      })

      return
    }

    const payload = {
      specialist_id: specialist.value,
      services_ids: services.map((s) => s.value),
      slot_interval: slotInterval,
      initial_date: initialDate,
      final_date: finalDate,
      start_hour: startHour,
      finish_hour: finishHour,
      ...(equipmentGroups.length
        ? { equipment_group_ids: equipmentGroups.map((group) => group.value) }
        : {})
    }

    searchMutation.mutate(payload)
  }

  /*
  ----------------------------
  WhatsApp
  ----------------------------
  */

  const openWhatsapp = () => {
    if (!phone) {
      toast({
        title: 'Informe o telefone',
        status: 'warning'
      })
      return
    }

    const encoded = encodeURIComponent(messagePreview)

    window.open(`https://wa.me/${phone}?text=${encoded}`, '_blank')
  }

  /*
  ----------------------------
  Limpar resultado
  ----------------------------
  */
  const clearResult = () => {
    setResultText('')
    setMessagePreview('')
    setVisualSlots([])
  }

  /*
  ----------------------------
  Atalho teclado
  ----------------------------
  */

  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter') {
        handleSearch()
      }
    }

    window.addEventListener('keydown', listener)

    return () => window.removeEventListener('keydown', listener)
  }, [specialist, services, slotInterval, initialDate, finalDate, startHour, finishHour])

  return (
    <>
      <Button colorScheme="green" size="sm" onClick={onOpen}>
        Horários disponíveis
      </Button>

      <ModalConfirmConsultTime
        title="Horários disponíveis"
        onCreate={handleSearch}
        isOpen={isOpen}
        onClose={onClose}
        isLoading={searchMutation.isLoading}
      >
        <VStack spacing={4}>
          <Input
            name="patientName"
            label="Nome do paciente"
            placeholder="Nome do paciente"
            value={patientName}
            onChange={(e) => setPatientName(e.target.value)}
          />

          <Input
            name="phone"
            label="Telefone WhatsApp"
            placeholder="Ex: +351912345678/+5511912345678"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />

          <SelectComponent
            name="specialist"
            label="Especialista"
            isClearable
            placeholder="Especialista"
            options={specialists}
            value={specialist}
            onChange={(v: any) => setSpecialist(v)}
          />

          <SelectComponent
            name="services"
            label="Serviços"
            placeholder="Serviços"
            isMulti
            options={servicesOptions}
            value={services}
            onChange={(v: any) => setServices(v as Option[])}
          />

          <SelectComponent
            name="equipmentGroups"
            label="Grupos de equipamentos"
            placeholder="Grupos de equipamentos (opcional)"
            isMulti
            options={equipmentGroupOptions}
            value={equipmentGroups}
            onChange={(v: any) => setEquipmentGroups((v || []) as Option[])}
          />

          <HStack w="100%">
            <Input
              name="initialDate"
              label="Data inicial"
              type="date"
              value={initialDate}
              onChange={(e) => setInitialDate(e.target.value)}
            />

            <Input
              name="finalDate"
              label="Data final"
              type="date"
              min={initialDate}
              max={maxFinalDateLimit}
              value={finalDate}
              onChange={(e) => setFinalDate(e.target.value)}
            />
          </HStack>

          <HStack w="100%">
            <Input
              name="slotInterval"
              label="Intervalo"
              type="number"
              min="15"
              placeholder="Intervalo"
              value={slotInterval}
              onChange={(e) => setSlotInterval(Number(e.target.value))}
              onBlur={(e) => setSlotInterval(Math.max(15, Number(e.target.value)))}
            />

            <Input
              name="startHour"
              label="Horário de início"
              type="time"
              value={startHour}
              onChange={(e) => setStartHour(e.target.value)}
            />

            <Input
              name="finishHour"
              label="Horário de término"
              type="time"
              value={finishHour}
              onChange={(e) => setFinishHour(e.target.value)}
            />
          </HStack>

          {searchMutation.isLoading && <Spinner />}

          {resultText && (
            <Flex w="100%" gap={4} overflow="auto" flexDirection="column">
              {visualSlots.length > 0 && (
                <Box w="100%" maxH="40vh" overflowY="auto" pr={1}>
                  <VStack align="stretch" spacing={3}>
                    {groupSlotsByDate(visualSlots).map((group, groupIndex) => (
                      <Box key={group.dateKey}>
                        {groupIndex > 0 && <Divider mb={3} />}
                        <Text
                          fontWeight="bold"
                          mb={2}
                          position="sticky"
                          top={0}
                          bg="white"
                          zIndex={1}
                        >
                          {group.title.replace(/:$/, '')}
                        </Text>
                        <VStack align="stretch" spacing={2}>
                          {group.slots.map((slot, index) => (
                            <Box
                              key={`${slot.arrival_time}-${index}`}
                              borderWidth="1px"
                              borderRadius="md"
                              p={3}
                            >
                              <Badge colorScheme="blue" mb={1}>
                                {slot.label ||
                                  `${formatHour(slot.initial_time, clinicTimezone)} → ${formatHour(
                                    slot.final_time,
                                    clinicTimezone
                                  )}`}
                              </Badge>
                              {Object.values(slot.available_equipments_by_group || {}).map(
                                (equipmentGroup) => (
                                  <Text key={equipmentGroup.group_id} fontSize="sm">
                                    {formatEquipmentGroupLine(equipmentGroup)}
                                  </Text>
                                )
                              )}
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}
              <Textarea
                name="resultText"
                label="Editar mensagem"
                value={resultText}
                onChange={(e) => {
                  setResultText(e.target.value)
                  setMessagePreview(buildMessagePreview(e.target.value))
                }}
              />

              <Box bg="gray.50" p={4} borderRadius="md" w="100%">
                <Text fontWeight="bold" mb={2}>
                  Preview WhatsApp
                </Text>

                <Text whiteSpace="pre-wrap">{messagePreview}</Text>
              </Box>

              <HStack>
                <Button
                  size="sm"
                  colorScheme="green"
                  onClick={() => {
                    onCopy()
                    toast({
                      title: 'Mensagem copiada'
                    })
                  }}
                >
                  Copiar mensagem
                </Button>

                <Button size="sm" colorScheme="whatsapp" onClick={openWhatsapp}>
                  Abrir WhatsApp
                </Button>

                <Button size="sm" colorScheme="red" onClick={clearResult}>
                  Limpar resultado
                </Button>
              </HStack>
            </Flex>
          )}
        </VStack>
      </ModalConfirmConsultTime>
    </>
  )
}
